import React, { useEffect, useMemo, useState } from 'react';
import { Footer, Header } from './headerFooter';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import { useNavigate } from 'react-router-dom';
import { getHost } from './config';
import 'bootstrap/dist/css/bootstrap.min.css';
import './custom.scss';

export const ADMIN_USERNAME = 'administrator';

export function usernameFromToken(token) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.username || null;
  } catch {
    return null;
  }
}

export function isAdministratorToken(token) {
  return usernameFromToken(token) === ADMIN_USERNAME;
}

function AdminUserPremium() {
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');
  const isAdmin = useMemo(() => isAdministratorToken(token), [token]);

  const [lookupMode, setLookupMode] = useState('username');
  const [lookupValue, setLookupValue] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);

  const [setMode, setSetMode] = useState('username');
  const [setValue, setSetValue] = useState('');
  const [setResult, setSetResult] = useState(null);
  const [setError, setSetError] = useState('');
  const [setLoading, setSetLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/', { replace: true });
      return;
    }
    if (!isAdmin) {
      navigate('/main', { replace: true });
    }
  }, [token, isAdmin, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.setItem('logout-event', Date.now());
    navigate('/', { replace: true });
  };

  const handleLookup = async (event) => {
    event.preventDefault();
    setLookupError('');
    setLookupResult(null);

    const value = lookupValue.trim();
    if (!value) {
      setLookupError('Συμπληρώστε username ή email.');
      return;
    }

    const params = new URLSearchParams();
    if (lookupMode === 'username') {
      params.set('username', value);
    } else {
      params.set('email', value);
    }

    setLookupLoading(true);
    try {
      const response = await fetch(`${getHost()}/api/admin/user-premium?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Αποτυχία αναζήτησης.');
      }
      setLookupResult(data);
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : 'Αποτυχία αναζήτησης.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSetPremium = async (event) => {
    event.preventDefault();
    setSetError('');
    setSetResult(null);

    const value = setValue.trim();
    if (!value) {
      setSetError('Συμπληρώστε username ή email.');
      return;
    }

    const body =
      setMode === 'username' ? { username: value } : { email: value };

    setSetLoading(true);
    try {
      const response = await fetch(`${getHost()}/api/admin/user-premium`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Αποτυχία ενημέρωσης.');
      }
      setSetResult(data);
      if (
        lookupResult &&
        ((lookupResult.username && lookupResult.username === data.username) ||
          (lookupResult.email && lookupResult.email === data.email))
      ) {
        setLookupResult({
          ...lookupResult,
          user_premium: true,
        });
      }
    } catch (err) {
      setSetError(err instanceof Error ? err.message : 'Αποτυχία ενημέρωσης.');
    } finally {
      setSetLoading(false);
    }
  };

  if (!token || !isAdmin) {
    return null;
  }

  return (
    <div id="root">
      <Header handleLogout={handleLogout} />
      <main className="main-container">
        <Container className="py-4" style={{ maxWidth: 720 }}>
          <h1 className="h4 mb-2">Διαχείριση Premium</h1>
          <p className="text-muted small mb-4">
            Ορατό μόνο στον χρήστη <code>{ADMIN_USERNAME}</code>. Αναζήτηση και χειροκίνητη
            ενεργοποίηση του <code>user_premium</code>.
          </p>

          <Card className="mb-4">
            <Card.Body>
              <Card.Title className="h6">Έλεγχος user_premium</Card.Title>
              <Form onSubmit={handleLookup}>
                <Form.Group className="mb-3" controlId="admin.lookupMode">
                  <Form.Label>Αναζήτηση με</Form.Label>
                  <Form.Select
                    className="custom-form"
                    value={lookupMode}
                    onChange={(e) => {
                      setLookupMode(e.target.value);
                      setLookupValue('');
                      setLookupResult(null);
                      setLookupError('');
                    }}
                  >
                    <option value="username">Username</option>
                    <option value="email">Email</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3" controlId="admin.lookupValue">
                  <Form.Label>{lookupMode === 'username' ? 'Username' : 'Email'}</Form.Label>
                  <Form.Control
                    className="custom-form"
                    type={lookupMode === 'email' ? 'email' : 'text'}
                    value={lookupValue}
                    onChange={(e) => setLookupValue(e.target.value)}
                    autoComplete="off"
                  />
                </Form.Group>
                {lookupError && <Alert variant="danger">{lookupError}</Alert>}
                {lookupResult && (
                  <Alert variant={lookupResult.user_premium ? 'success' : 'warning'}>
                    <div>
                      <strong>Username:</strong> {lookupResult.username}
                    </div>
                    <div>
                      <strong>Email:</strong> {lookupResult.email}
                    </div>
                    <div>
                      <strong>userId:</strong> {lookupResult.userId}
                    </div>
                    <div>
                      <strong>user_premium:</strong>{' '}
                      {lookupResult.user_premium ? '1 (ενεργό)' : '0 (ανενεργό)'}
                    </div>
                    <div>
                      <strong>is_verified:</strong>{' '}
                      {lookupResult.is_verified ? 'ναι' : 'όχι'}
                    </div>
                  </Alert>
                )}
                <Button type="submit" variant="primary" disabled={lookupLoading}>
                  {lookupLoading ? 'Αναζήτηση…' : 'Αναζήτηση'}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <Card.Title className="h6">Ενεργοποίηση Premium (user_premium = 1)</Card.Title>
              <Form onSubmit={handleSetPremium}>
                <Form.Group className="mb-3" controlId="admin.setMode">
                  <Form.Label>Ορισμός με</Form.Label>
                  <Form.Select
                    className="custom-form"
                    value={setMode}
                    onChange={(e) => {
                      setSetMode(e.target.value);
                      setSetValue('');
                      setSetResult(null);
                      setSetError('');
                    }}
                  >
                    <option value="username">Username</option>
                    <option value="email">Email</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3" controlId="admin.setValue">
                  <Form.Label>{setMode === 'username' ? 'Username' : 'Email'}</Form.Label>
                  <Form.Control
                    className="custom-form"
                    type={setMode === 'email' ? 'email' : 'text'}
                    value={setValue}
                    onChange={(e) => setSetValue(e.target.value)}
                    autoComplete="off"
                  />
                </Form.Group>
                {setError && <Alert variant="danger">{setError}</Alert>}
                {setResult && (
                  <Alert variant="success">
                    <div>{setResult.message}</div>
                    <div>
                      <strong>Username:</strong> {setResult.username}
                    </div>
                    <div>
                      <strong>Email:</strong> {setResult.email}
                    </div>
                    <div>
                      <strong>user_premium:</strong>{' '}
                      {setResult.user_premium ? '1 (ενεργό)' : '0'}
                    </div>
                    <div>
                      <strong>Ήταν ήδη premium:</strong>{' '}
                      {setResult.was_premium ? 'ναι' : 'όχι'}
                    </div>
                  </Alert>
                )}
                <Button type="submit" variant="warning" disabled={setLoading}>
                  {setLoading ? 'Ενημέρωση…' : 'Ορισμός user_premium = 1'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Container>
      </main>
      <Footer />
    </div>
  );
}

export default AdminUserPremium;
